import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from 'components/Dialog';
import { Button, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { XGrid } from '@material-ui/x-grid';
import { getLoginAttemptsByUser, markLoginAttemptsViewed } from 'Settings/Security/SecFunctions';
import { handleError } from 'reducers/ErrorReducer';
import { displayDateTime } from 'reducers/TimeReducer';
import UAParser from 'ua-parser-js';

const useStyles = makeStyles(theme => ({
  grid: {
    width: '100%',
    height: 500,
    marginTop: theme.spacing(2),
  },
  noData: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

const parseUserAgent = uaString => {
  if (!uaString) return 'Unknown';

  const parser = new UAParser(uaString);
  const result = parser.getResult();

  const browser = result.browser.name
    ? `${result.browser.name} ${result.browser.version || ''}`
    : '';
  const os = result.os.name ? `${result.os.name} ${result.os.version || ''}` : '';

  return `${browser}${browser && os ? ' on ' : ''}${os}`.trim() || 'Unknown';
};

const columns = [
  {
    field: 'AttemptDateTime',
    headerName: 'Attempt Date/Time',
    width: 200,
    valueFormatter: params => displayDateTime(params.value),
  },
  {
    field: 'UserIP',
    headerName: 'IP Address',
    width: 150,
  },
  {
    field: 'UserAgent',
    headerName: 'User Agent',
    width: 300,
    valueFormatter: params => parseUserAgent(params.value),
  },
  {
    field: 'ApplicationName',
    headerName: 'Application',
    width: 120,
  },
  {
    field: 'ViewedBy',
    headerName: 'Viewed By',
    width: 150,
  },
  {
    field: 'ViewedDateTime',
    headerName: 'Viewed Date/Time',
    width: 200,
    valueFormatter: params => (params.value ? displayDateTime(params.value) : ''),
  },
];

const LoginAttemptsModal = props => {
  const classes = useStyles();
  const { ptsUserID, username, currentUser, onClose, onViewComplete } = props;
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttempts();
  }, [ptsUserID]);

  const fetchAttempts = async () => {
    try {
      setLoading(true);
      const result = await getLoginAttemptsByUser(ptsUserID);

      const processedData = result.data.map(row => ({
        ...row,
        id: row.ptsSecUserLoginAttemptsID,
      }));

      setAttempts(processedData);

      // Mark unviewed attempts as viewed
      const hasUnviewed = result.data.some(attempt => !attempt.IsViewed);
      if (hasUnviewed && currentUser) {
        await markLoginAttemptsViewed(ptsUserID, currentUser.Username);
        // Refresh data to show updated viewed status
        const updatedResult = await getLoginAttemptsByUser(ptsUserID);
        const updatedData = updatedResult.data.map(row => ({
          ...row,
          id: row.ptsSecUserLoginAttemptsID,
        }));
        setAttempts(updatedData);

        // Notify parent to refresh counts
        if (onViewComplete) {
          onViewComplete();
        }
      }
    } catch (error) {
      console.error('Error fetching login attempts:', error);
      props.handleError(error, 'Error loading login attempts');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const renderActions = () => {
    return (
      <>
        <Button color="primary" onClick={handleClose}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  return (
    <Dialog
      toolbar
      onClose={handleClose}
      title={`Login Attempts - ${username || 'User'}`}
      actions={renderActions()}
      maxWidth="lg"
      fullWidth>
      {loading ? (
        <Typography className={classes.noData}>Loading...</Typography>
      ) : attempts.length === 0 ? (
        <Typography className={classes.noData}>No login attempts found for this user.</Typography>
      ) : (
        <div className={classes.grid}>
          <XGrid
            columns={columns}
            rows={attempts}
            rowHeight={38}
            loading={loading}
            hideFooter={attempts.length <= 100}
            pageSize={100}
            rowsPerPageOptions={[100]}
          />
        </div>
      )}
    </Dialog>
  );
};

const mapStateToProps = state => {
  return {
    currentUser: state.user?.userData?.user || null,
  };
};

export default connect(mapStateToProps, { handleError })(LoginAttemptsModal);
